import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ImageSearchOutlinedIcon from "@mui/icons-material/ImageSearchOutlined";
import {
  CSSProperties,
  DragEvent,
  PointerEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import {
  selectSelectedElement,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import { AssetDetailsDTO, AssetKey } from "../../../dto/AssetDTO";
import { GetNewGuid } from "../../../helpers/GuidHelper";
import { useAssetPreview } from "../../../hooks/AssetPreviewHook";
import {
  LABEL_DRAG_AND_DROP_QUIZ_MAX_LABELS,
  ILabelDragAndDropQuiz,
  ILabelDragAndDropQuizDropZone,
  ILabelDragAndDropQuizLabel,
} from "../../../models/interactions/LabelDragAndDropQuiz";
import FileExplorerDialog from "../../file_explorer/FileExplorerDialog";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import PiBasicQuizAddAnswerButtonV2 from "../components/v2/PiBasicQuizAddAnswerButtonV2";
import PiBasicQuizAnswerTextfieldV2 from "../components/v2/PiBasicQuizAnswerTextfieldV2";
import "./LabelDragAndDropQuizPreviewBody.css";
import { toLayoutPx } from "../../../helpers/screenScale";

type ActiveZoneDrag = {
  mode: "move" | "resize";
  pointerId: number;
  startClientX: number;
  startClientY: number;
  zone: ILabelDragAndDropQuizDropZone;
};

type ImageOverlayBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

const LABEL_DRAG_TYPE = "application/x-pi-label-id";
const MAX_ZONES = 15;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const defaultDropZoneTextColor = { r: 255, g: 255, b: 255, a: 1 };

const isRgbaColor = (
  value: unknown,
): value is { r: number; g: number; b: number; a: number } => {
  if (typeof value !== "object" || value === null) return false;
  const color = value as { r?: unknown; g?: unknown; b?: unknown; a?: unknown };
  return (
    typeof color.r === "number" &&
    typeof color.g === "number" &&
    typeof color.b === "number" &&
    typeof color.a === "number"
  );
};

const getDropZoneTextColor = (quiz: ILabelDragAndDropQuiz) => {
  const value = quiz.properties?.find((property) => property.id === "textColor")
    ?.value;
  const color = isRgbaColor(value) ? value : defaultDropZoneTextColor;
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
};

const getLabelLetter = (index: number) => {
  let value = index + 1;
  let result = "";

  while (value > 0) {
    value -= 1;
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26);
  }

  return result;
};

const createDefaultDropZone = (
  correctLabelId?: string,
): ILabelDragAndDropQuizDropZone => ({
  id: GetNewGuid(),
  correctLabelId,
  xPos: 0.5,
  yPos: 0.5,
  xSize: 0.18,
  ySize: 0.14,
});

function Section(props: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col flex-1 min-h-0 gap-[10px]">
      <div className="text-[14px] font-semibold text-interaction-primary">
        {props.title}
      </div>
      <div className="flex flex-col min-h-0 gap-[8px]">{props.children}</div>
    </div>
  );
}

function DeleteButton(props: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      aria-label={props.label}
      className="flex flex-shrink-0 w-[32px] h-[32px] text-interaction-primary rounded-[5px] hover:bg-interaction-primary-washed disabled:text-interaction-disabled disabled:cursor-not-allowed"
      disabled={props.disabled}
      onClick={props.onClick}
      type="button"
    >
      <DeleteOutlineIcon className="m-auto" fontSize="small" />
    </button>
  );
}

export default function LabelDragAndDropQuizPreviewBody() {
  const quiz = useSelector(selectSelectedElement) as ILabelDragAndDropQuiz;
  const dispatch = useDispatch();
  const { getValue, getNewKey } = useTrainingLocalization();
  const assetPreview = useAssetPreview(quiz.imageAssetId);
  const previewUrl = assetPreview?.getAssetPreview() ?? "";
  const imageStageRef = useRef<HTMLDivElement | null>(null);
  const imageOverlayRef = useRef<HTMLDivElement | null>(null);
  const backgroundImageRef = useRef<HTMLImageElement | null>(null);
  const [fileExplorerOpen, setFileExplorerOpen] = useState(false);
  const [activeDrag, setActiveDrag] = useState<ActiveZoneDrag | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | undefined>(
    quiz.dropZones[0]?.id,
  );
  const [placementLabelId, setPlacementLabelId] = useState<string | undefined>(
    undefined,
  );
  const [dragOverZoneId, setDragOverZoneId] = useState<string | undefined>(
    undefined,
  );
  const [localZoneOverride, setLocalZoneOverride] =
    useState<ILabelDragAndDropQuizDropZone | null>(null);
  const [imageOverlayBounds, setImageOverlayBounds] = useState<
    ImageOverlayBounds | undefined
  >(undefined);

  const selectedZone =
    quiz.dropZones.find((dropZone) => dropZone.id === selectedZoneId) ??
    quiz.dropZones[0];
  const dropZoneTextColor = getDropZoneTextColor(quiz);
  const canAddLabel = quiz.labels.length < LABEL_DRAG_AND_DROP_QUIZ_MAX_LABELS;

  const measureImageOverlayBounds = useCallback(() => {
    const stage = imageStageRef.current;
    const image = backgroundImageRef.current;
    if (!stage || !image) return;

    const stageBounds = stage.getBoundingClientRect();
    const imageBounds = image.getBoundingClientRect();
    if (
      !stageBounds.width ||
      !stageBounds.height ||
      !imageBounds.width ||
      !imageBounds.height
    ) {
      return;
    }

    const nextBounds = {
      left: toLayoutPx(imageBounds.left - stageBounds.left),
      top: toLayoutPx(imageBounds.top - stageBounds.top),
      width: toLayoutPx(imageBounds.width),
      height: toLayoutPx(imageBounds.height),
    };

    setImageOverlayBounds((previousBounds) =>
      previousBounds &&
      previousBounds.left === nextBounds.left &&
      previousBounds.top === nextBounds.top &&
      previousBounds.width === nextBounds.width &&
      previousBounds.height === nextBounds.height
        ? previousBounds
        : nextBounds,
    );
  }, []);

  useEffect(() => {
    if (!placementLabelId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPlacementLabelId(undefined);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [placementLabelId]);

  useEffect(() => {
    if (!quiz.imageAssetId) {
      setImageOverlayBounds(undefined);
      return;
    }

    measureImageOverlayBounds();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureImageOverlayBounds);
      return () =>
        window.removeEventListener("resize", measureImageOverlayBounds);
    }

    const resizeObserver = new ResizeObserver(measureImageOverlayBounds);
    if (imageStageRef.current) resizeObserver.observe(imageStageRef.current);
    if (backgroundImageRef.current) {
      resizeObserver.observe(backgroundImageRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [measureImageOverlayBounds, previewUrl, quiz.imageAssetId]);

  const updateQuiz = (updatedQuiz: ILabelDragAndDropQuiz) => {
    dispatch(updateSelectedInteraction(updatedQuiz));
  };

  const updateDropZones = (dropZones: ILabelDragAndDropQuizDropZone[]) => {
    updateQuiz({ ...quiz, dropZones });
  };

  const updateDropZone = (dropZone: ILabelDragAndDropQuizDropZone) => {
    updateDropZones(
      quiz.dropZones.map((existingDropZone) =>
        existingDropZone.id === dropZone.id ? dropZone : existingDropZone,
      ),
    );
  };

  const debouncedCommitDropZone = useDebouncedCallback(
    (zone: ILabelDragAndDropQuizDropZone) => updateDropZone(zone),
    100,
  );

  const assignLabelToZone = (labelId: string, dropZoneId: string) => {
    updateDropZones(
      quiz.dropZones.map((existingDropZone) => ({
        ...existingDropZone,
        correctLabelId:
          existingDropZone.id === dropZoneId
            ? labelId
            : existingDropZone.correctLabelId === labelId
              ? undefined
              : existingDropZone.correctLabelId,
      })),
    );
    setSelectedZoneId(dropZoneId);
  };

  const clearZoneAssignment = (dropZoneId: string) => {
    updateDropZones(
      quiz.dropZones.map((existingDropZone) =>
        existingDropZone.id === dropZoneId
          ? { ...existingDropZone, correctLabelId: undefined }
          : existingDropZone,
      ),
    );
  };

  const addLabel = () => {
    if (quiz.labels.length >= LABEL_DRAG_AND_DROP_QUIZ_MAX_LABELS) return;

    updateQuiz({
      ...quiz,
      labels: [...quiz.labels, { id: GetNewGuid(), text: getNewKey() }],
    });
  };

  const deleteLabel = (labelId: string) => {
    const labels = quiz.labels.filter((label) => label.id !== labelId);
    const dropZones = quiz.dropZones.map((dropZone) => ({
      ...dropZone,
      correctLabelId:
        dropZone.correctLabelId === labelId
          ? undefined
          : dropZone.correctLabelId,
    }));

    if (placementLabelId === labelId) setPlacementLabelId(undefined);
    updateQuiz({ ...quiz, labels, dropZones });
  };

  const onSelectImage = (asset: AssetDetailsDTO) => {
    updateQuiz({ ...quiz, imageAssetId: asset.assetId });
    setFileExplorerOpen(false);
  };

  const onBackgroundImageLoad = () => {
    measureImageOverlayBounds();
  };

  const addDropZone = () => {
    const dropZone = createDefaultDropZone();

    updateQuiz({
      ...quiz,
      dropZones: [...quiz.dropZones, dropZone],
    });
    setSelectedZoneId(dropZone.id);
  };

  const deleteDropZone = (dropZoneId: string) => {
    const dropZones = quiz.dropZones.filter(
      (dropZone) => dropZone.id !== dropZoneId,
    );

    updateQuiz({ ...quiz, dropZones });
    if (selectedZoneId === dropZoneId) {
      setSelectedZoneId(dropZones[0]?.id);
    }
  };

  const constrainZone = (
    zone: ILabelDragAndDropQuizDropZone,
  ): ILabelDragAndDropQuizDropZone => {
    const xSize = clamp(zone.xSize, 0.04, 1);
    const ySize = clamp(zone.ySize, 0.04, 1);

    return {
      ...zone,
      xSize,
      ySize,
      xPos: clamp(zone.xPos, xSize / 2, 1 - xSize / 2),
      yPos: clamp(zone.yPos, ySize / 2, 1 - ySize / 2),
    };
  };

  const updateActiveZoneFromPointer = (
    event: PointerEvent<HTMLDivElement>,
    drag: ActiveZoneDrag,
  ) => {
    const bounds = imageOverlayRef.current?.getBoundingClientRect();
    if (!bounds?.width || !bounds?.height) return;

    const xDiff = (event.clientX - drag.startClientX) / bounds.width;
    const yDiff = (event.clientY - drag.startClientY) / bounds.height;
    const updatedZone =
      drag.mode === "move"
        ? {
            ...drag.zone,
            xPos: drag.zone.xPos + xDiff,
            yPos: drag.zone.yPos + yDiff,
          }
        : {
            ...drag.zone,
            xSize: drag.zone.xSize + xDiff,
            ySize: drag.zone.ySize + yDiff,
          };

    const constrained = constrainZone(updatedZone);
    setLocalZoneOverride(constrained);
    debouncedCommitDropZone(constrained);
  };

  const onZonePointerDown = (
    event: PointerEvent<HTMLDivElement>,
    zone: ILabelDragAndDropQuizDropZone,
    mode: ActiveZoneDrag["mode"],
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedZoneId(zone.id);

    if (placementLabelId && mode === "move") {
      assignLabelToZone(placementLabelId, zone.id);
      setPlacementLabelId(undefined);
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    setActiveDrag({
      mode,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      zone,
    });
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!activeDrag || activeDrag.pointerId !== event.pointerId) return;
    updateActiveZoneFromPointer(event, activeDrag);
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!activeDrag || activeDrag.pointerId !== event.pointerId) return;
    setActiveDrag(null);
    debouncedCommitDropZone.flush();
    setLocalZoneOverride(null);
  };

  const onLabelDragStart = (
    event: DragEvent<HTMLDivElement>,
    labelId: string,
  ) => {
    event.dataTransfer.setData(LABEL_DRAG_TYPE, labelId);
    event.dataTransfer.effectAllowed = "move";
  };

  const onLabelDragEnd = () => {
    setDragOverZoneId(undefined);
  };

  const onZoneDragOver = (
    event: DragEvent<HTMLDivElement>,
    dropZone: ILabelDragAndDropQuizDropZone,
  ) => {
    if (!event.dataTransfer.types.includes(LABEL_DRAG_TYPE)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (dragOverZoneId !== dropZone.id) setDragOverZoneId(dropZone.id);
  };

  const onZoneDragLeave = (dropZone: ILabelDragAndDropQuizDropZone) => {
    if (dragOverZoneId === dropZone.id) setDragOverZoneId(undefined);
  };

  const onZoneDrop = (
    event: DragEvent<HTMLDivElement>,
    dropZone: ILabelDragAndDropQuizDropZone,
  ) => {
    event.preventDefault();
    const labelId = event.dataTransfer.getData(LABEL_DRAG_TYPE);
    setDragOverZoneId(undefined);
    if (!labelId) return;
    assignLabelToZone(labelId, dropZone.id);
  };

  const renderLabel = (label: ILabelDragAndDropQuizLabel, index: number) => {
    const assignedZoneIndex = quiz.dropZones.findIndex(
      (dropZone) => dropZone.correctLabelId === label.id,
    );
    const assignedZone =
      assignedZoneIndex >= 0 ? quiz.dropZones[assignedZoneIndex] : undefined;
    const isPlacing = placementLabelId === label.id;

    return (
      <div
        className={
          "labeldndquizpreview-label-row flex w-full rounded-[6px] p-[2px] " +
          (isPlacing ? "bg-interaction-primary-washed" : "")
        }
        key={label.id}
      >
        <div className="labeldndquizpreview-label-card flex flex-col flex-1 min-w-0 gap-[6px] px-[8px] py-[7px] bg-white dark:bg-creator-surface rounded-[5px] border border-solid border-interaction-primary-washed dark:border-creator-border">
          <div className="labeldndquizpreview-label-text w-full min-w-0">
            <PiBasicQuizAnswerTextfieldV2 localizationKey={label.text} />
          </div>
          <div className="labeldndquizpreview-label-controls flex items-center gap-[6px]">
            <div
              aria-label={
                isPlacing
                  ? `Cancel placement for label ${index + 1}`
                  : `Drag label ${index + 1} onto a zone`
              }
              className={
                "flex flex-shrink-0 items-center justify-center w-[28px] h-[28px] rounded-full bg-interaction-primary text-white cursor-grab active:cursor-grabbing select-none hover:bg-interaction-primary-hover " +
                (isPlacing ? "ring-2 ring-offset-2 ring-interaction-primary " : "")
              }
              data-testid={`label-dnd-label-handle-${label.id}`}
              draggable
              onClick={() =>
                setPlacementLabelId(isPlacing ? undefined : label.id)
              }
              onDragEnd={onLabelDragEnd}
              onDragStart={(event) => onLabelDragStart(event, label.id)}
              role="button"
              title={
                isPlacing
                  ? "Click a zone to assign, or press Esc to cancel"
                  : "Drag onto a zone"
              }
            >
              <DragIndicatorIcon fontSize="small" />
            </div>
            {assignedZone ? (
              <button
                aria-label={`Unassign label ${index + 1} from zone ${getLabelLetter(assignedZoneIndex)}`}
                className="flex flex-shrink-0 items-center justify-center w-[24px] h-[24px] rounded-full bg-interaction-primary text-white hover:bg-interaction-primary-hover"
                onClick={() => clearZoneAssignment(assignedZone.id)}
                title={`Unassign from zone ${getLabelLetter(assignedZoneIndex)}`}
                type="button"
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            ) : null}
            <div className="labeldndquizpreview-control-spacer flex-1" />
            <DeleteButton
              disabled={quiz.labels.length <= 1}
              label="Delete label"
              onClick={() => deleteLabel(label.id)}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderZone = (
    dropZone: ILabelDragAndDropQuizDropZone,
    index: number,
  ) => {
    const effectiveZone =
      localZoneOverride && localZoneOverride.id === dropZone.id
        ? localZoneOverride
        : dropZone;
    const label = quiz.labels.find(
      (item) => item.id === dropZone.correctLabelId,
    );
    const isSelected = selectedZone?.id === dropZone.id;
    const isDropTarget = dragOverZoneId === dropZone.id;
    const zoneLetter = getLabelLetter(index);
    const assignedLabelText = label ? getValue(label.text) : "";

    return (
      <div
        className={
          "absolute flex items-center justify-center rounded-[5px] text-white cursor-grab touch-none " +
          (isSelected ? "drop-shadow-[0_0_4px_var(--pi-game-blue)] " : "") +
          (isDropTarget ? "ring-[3px] ring-white " : "")
        }
        data-testid={`label-dnd-zone-${dropZone.id}`}
        key={dropZone.id}
        onDragLeave={() => onZoneDragLeave(dropZone)}
        onDragOver={(event) => onZoneDragOver(event, dropZone)}
        onDrop={(event) => onZoneDrop(event, dropZone)}
        onPointerDown={(event) => onZonePointerDown(event, dropZone, "move")}
        style={{
          left: `${effectiveZone.xPos * 100}%`,
          top: `${effectiveZone.yPos * 100}%`,
          width: `${effectiveZone.xSize * 100}%`,
          height: `${effectiveZone.ySize * 100}%`,
          transform: "translate(-50%, -50%)",
          backgroundColor: "rgba(81, 130, 158, 0.28)",
        }}
      >
        <div
          className="absolute left-0 top-0 h-[4px] w-full rounded-t-[5px] bg-[repeating-linear-gradient(90deg,#51829E_0px,#51829E_8px,transparent_8px,transparent_14px)]"
          data-testid={`label-dnd-zone-border-top-${dropZone.id}`}
        />
        <div className="absolute left-0 bottom-0 h-[4px] w-full rounded-b-[5px] bg-[repeating-linear-gradient(90deg,#51829E_0px,#51829E_8px,transparent_8px,transparent_14px)]" />
        <div className="absolute left-0 top-0 h-full w-[4px] rounded-l-[5px] bg-[repeating-linear-gradient(180deg,#51829E_0px,#51829E_8px,transparent_8px,transparent_14px)]" />
        <div className="absolute right-0 top-0 h-full w-[4px] rounded-r-[5px] bg-[repeating-linear-gradient(180deg,#51829E_0px,#51829E_8px,transparent_8px,transparent_14px)]" />
        <div
          className="absolute left-[6px] top-[6px] z-[1] flex items-center justify-center min-w-[28px] h-[28px] px-[8px] text-[13px] font-semibold text-white bg-transparent"
          data-testid={`label-dnd-zone-letter-${dropZone.id}`}
          style={{ color: dropZoneTextColor }}
        >
          {zoneLetter}
        </div>
        {assignedLabelText && (
          <div
            className="relative z-[1] px-[8px] py-[4px] text-[15px] font-semibold text-white bg-transparent max-w-[calc(100%-16px)] text-center truncate"
            data-testid={`label-dnd-zone-label-${dropZone.id}`}
            style={{ color: dropZoneTextColor }}
          >
            {assignedLabelText}
          </div>
        )}
        <div
          aria-label={`Resize zone ${index + 1}`}
          className="absolute z-[2] right-[-7px] bottom-[-7px] w-[18px] h-[18px] cursor-nwse-resize bg-white rounded-full border-[2px] border-interaction-primary shadow-[0_1px_4px_rgba(0,0,0,0.18)] touch-none after:content-[''] after:absolute after:right-[4px] after:bottom-[4px] after:w-[6px] after:h-[6px] after:border-r-[2px] after:border-b-[2px] after:border-interaction-primary"
          data-testid={`label-dnd-zone-resize-${dropZone.id}`}
          onPointerDown={(event) => onZonePointerDown(event, dropZone, "resize")}
        />
        {isSelected && quiz.dropZones.length > 1 && (
          <button
            aria-label={`Delete drop zone ${zoneLetter}`}
            className="absolute z-[3] right-[-10px] top-[-10px] flex items-center justify-center w-[24px] h-[24px] text-interaction-primary bg-white rounded-full border-[2px] border-interaction-primary shadow-[0_1px_4px_rgba(0,0,0,0.18)] hover:bg-interaction-primary-washed"
            data-testid={`label-dnd-zone-delete-${dropZone.id}`}
            onClick={(event) => {
              event.stopPropagation();
              deleteDropZone(dropZone.id);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            type="button"
          >
            <CloseIcon fontSize="inherit" />
          </button>
        )}
      </div>
    );
  };

  const imagePickerButton = (
    <button
      aria-label={quiz.imageAssetId ? "Change image" : "Select image"}
      className="absolute flex right-[16px] bottom-[16px] bg-interaction-primary text-white text-[28px] w-[44px] h-[44px] rounded-full cursor-pointer hover:opacity-75"
      onClick={() => setFileExplorerOpen(true)}
      type="button"
    >
      {quiz.imageAssetId ? (
        <ImageSearchOutlinedIcon className="m-auto" fontSize="inherit" />
      ) : (
        <AddPhotoAlternateOutlinedIcon className="m-auto" fontSize="inherit" />
      )}
    </button>
  );

  const imageOverlayStyle: CSSProperties = imageOverlayBounds
    ? {
        left: `${imageOverlayBounds.left}px`,
        top: `${imageOverlayBounds.top}px`,
        width: `${imageOverlayBounds.width}px`,
        height: `${imageOverlayBounds.height}px`,
      }
    : {
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
      };

  return (
    <>
      <div className="labeldndquizpreview-root flex w-full h-full min-h-0 gap-[16px] p-[16px] bg-interaction-primary-washed rounded-[5px]">
        <div className="relative flex w-[72%] min-h-0 bg-white dark:bg-creator-surface rounded-[5px] overflow-hidden dark:border dark:border-solid dark:border-creator-border">
          {!quiz.imageAssetId && (
            <button
              className="flex flex-col items-center justify-center w-full h-full gap-[12px] text-interaction-primary hover:bg-interaction-primary-washed"
              data-testid="label-dnd-image-empty"
              onClick={() => setFileExplorerOpen(true)}
              type="button"
            >
              <AddPhotoAlternateOutlinedIcon fontSize="large" />
              <span className="text-[16px] font-semibold">Select image</span>
            </button>
          )}
          {quiz.imageAssetId && (
            <div
              className="relative flex items-center justify-center w-full h-full p-[16px]"
              data-testid="label-dnd-stage"
              ref={imageStageRef}
            >
              <img
                alt="Label Drag And Drop Quiz"
                className="block max-w-full max-h-full object-contain"
                onLoad={onBackgroundImageLoad}
                ref={backgroundImageRef}
                src={previewUrl}
              />
              <div
                className="absolute"
                data-testid="label-dnd-zone-overlay"
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                ref={imageOverlayRef}
                style={imageOverlayStyle}
              >
                {quiz.dropZones.map(renderZone)}
              </div>
            </div>
          )}
          {quiz.imageAssetId && (
            <div className="absolute left-[16px] bottom-[16px] z-[3] flex gap-[8px]">
              <button
                aria-label="Add drop zone"
                className="flex items-center justify-center w-[44px] h-[44px] text-[28px] font-semibold text-white bg-interaction-primary rounded-full hover:bg-interaction-primary-hover disabled:bg-interaction-disabled"
                disabled={quiz.dropZones.length >= MAX_ZONES}
                onClick={addDropZone}
                title="Add drop zone"
                type="button"
              >
                +
              </button>
            </div>
          )}
          {imagePickerButton}
        </div>
        <div className="flex flex-col w-[28%] min-h-0 gap-[12px]">
          <Section
            title={`Labels (${quiz.labels.length} / ${LABEL_DRAG_AND_DROP_QUIZ_MAX_LABELS})`}
          >
            <div className="flex flex-col flex-1 min-h-[140px] gap-[6px] overflow-y-auto">
              {quiz.labels.map(renderLabel)}
            </div>
            {canAddLabel && (
              <PiBasicQuizAddAnswerButtonV2
                onClick={addLabel}
                text="Add label"
              />
            )}
          </Section>
        </div>
      </div>

      <FileExplorerDialog
        open={fileExplorerOpen}
        onClose={() => setFileExplorerOpen(false)}
        onSelect={onSelectImage}
        type={AssetKey.Image}
      />
    </>
  );
}
